import { HoverBorderButton, StyledButton } from '@elements/Button';
import {
  AccentText,
  BlueDarkText,
  BlueText,
  BoldText,
  FlexColumn,
  MuteText,
  PinkText,
  PurpleText,
  StyledCheckbox as SC,
  SuccessDarkText,
  YellowText,
} from '@elements/Common';
import { NumberMaskInput } from '@elements/CustomMaskInput';
import { Flag } from '@elements/Flag';
import { DarkPopup, SimpleDarkToolTip } from '@elements/Popup';
import { StyledFormTitle } from '@elements/Title';
import { tooltTipsText } from '@page-components/condensed-timeline/Chart';
import { POWindowVelocityRenderer } from '@page-components/purchase-order/timeline/VelocityRederer';
import { CommonUtility } from '@utility/common';
import { ErrorMessageConstant } from '@utility/constants/common';
import ItemService from '@utility/services/item';
import PredictionService from '@utility/services/prediction';
import { ToastMessage } from '@utility/toast';
import {
  ArrowCounterClockwise,
  CaretDown,
  CaretRight,
  Check,
  Info,
  ShoppingCart,
  Truck,
} from '@phosphor-icons/react';
import { cn } from '@skupreme/shared/ui';
import React, {
  type ComponentPropsWithoutRef,
  type ReactNode,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  FormulaContainer,
  parseNumber,
  RenderMutedDays,
  StyledPopup,
} from './formulaCommon';
import { FCInventory, FCInventorySummary } from './FCInventory';

type DivProps = ComponentPropsWithoutRef<'div'>;

/*
 * The sections open and close from their own carets, and a closed one keeps
 * its content mounted but hidden, as Semantic's accordion content did.
 */
function SectionTitle({ className, ...props }: DivProps) {
  return (
    <div
      className={cn(
        'sk:flex sk:w-full sk:items-center sk:justify-between sk:pt-2 sk:[&_h4]:m-0',
        className,
      )}
      {...props}
    />
  );
}

function SectionContent({
  active,
  className,
  ...props
}: DivProps & { active: boolean }) {
  return <div className={cn(!active && 'sk:hidden', className)} {...props} />;
}

function ManualStockIcon({ className }: { className?: string }) {
  return (
    <img
      src="/images/fp/manualDark.svg"
      width={20}
      alt=""
      className={cn('sk:inline-block', className)}
    />
  );
}

export function SeasonalityAccordion({
  allowed = true,
  lightningDeal,
  loading,
  readOnly,
  region,
  seasonalSpike,
  setSeasonPopup,
  showTitle = true,
  tabs,
  toggleTab,
}) {
  const seasonalUnits = useMemo(() => {
    if (seasonalSpike.length < 1) return 0;

    let units = 0;
    seasonalSpike.forEach(season => {
      units += season.additionalUnits || 0;
    });
    return units;
  }, [seasonalSpike]);

  const lightningUnits = useMemo(() => {
    if (lightningDeal.length < 1) return 0;

    let units = 0;
    lightningDeal.forEach(lightning => {
      units += lightning.units || 0;
    });
    return units;
  }, [lightningDeal]);

  return (
    <div
      className={cn(
        !allowed && 'sk:cursor-not-allowed! sk:opacity-80 sk:hover:opacity-100',
      )}
    >
      {showTitle && (
        <SectionTitle className="sk:mb-2">
          <h4 className="sk:flex sk:items-center">
            {' '}
            {allowed ? (
              'Seasonality and Lightning Deals'
            ) : (
              <>
                <BoldText className="sk:flex! sk:items-center! sk:cursor-default">
                  Seasonality and Lightning Deals
                </BoldText>
                <DarkPopup
                  trigger={
                    <Info className="sk:ml-2 sk:cursor-pointer sk:text-danger" />
                  }
                  position="top right"
                >
                  {tooltTipsText.notAllowed}
                </DarkPopup>
              </>
            )}
          </h4>
          {seasonalSpike.length === 0 && lightningDeal.length === 0 ? (
            allowed ? (
              <div>
                <HoverBorderButton
                  size="mini"
                  className="sk:cursor-default"
                  disabled={readOnly || loading}
                  onClick={() => setSeasonPopup(true)}
                >
                  Edit
                </HoverBorderButton>
              </div>
            ) : (
              <DarkPopup
                trigger={
                  <div>
                    <HoverBorderButton
                      size="mini"
                      className="sk:cursor-default"
                      disabled
                    >
                      Edit
                    </HoverBorderButton>
                  </div>
                }
                position="top right"
              >
                {tooltTipsText.notAllowed}
              </DarkPopup>
            )
          ) : tabs.season ? (
            <CaretDown onClick={() => toggleTab('season', !tabs.season)} />
          ) : (
            <CaretRight onClick={() => toggleTab('season', !tabs.season)} />
          )}
        </SectionTitle>
      )}
      <SectionContent
        active={tabs.season}
        className="sk:text-xs sk:[&_.strike-through]:line-through"
      >
        <div className="sk:flex! sk:justify-between! sk:w-full">
          <div className="sk:flex!">
            {lightningDeal.length > 0 && (
              <div className="sk:flex! sk:flex-col! sk:mr-5!">
                <div>
                  <MuteText className="sk:mb-1!">
                    Future Lightning Deals
                  </MuteText>
                  {region && <Flag className="sk:ml-2!" name={region} />}
                </div>
                <div className="sk:mt-1!">
                  <BoldText>
                    {CommonUtility.numberWithCommas(lightningUnits)} Units
                  </BoldText>
                  <MuteText className="sk:mx-2!">
                    ({lightningDeal?.length} Lightning Deal)
                  </MuteText>
                </div>
              </div>
            )}
            {seasonalSpike.length > 0 ? (
              <div
                className={`sk:flex! sk:flex-col! ${
                  lightningDeal.length > 0 && 'sk:ml-5! sk:pl-5!'
                }`}
              >
                <div>
                  <MuteText className="sk:mb-1!">
                    Future Seasonal Spikes
                  </MuteText>
                  {region && <Flag className="sk:ml-2!" name={region} />}
                </div>
                <div className="sk:mt-1!">
                  <BoldText>
                    {CommonUtility.numberWithCommas(Math.round(seasonalUnits))}{' '}
                    Units
                  </BoldText>
                  <MuteText className="sk:mx-2!">
                    ({seasonalSpike.length} Seasonal Spike)
                  </MuteText>
                </div>
              </div>
            ) : (
              <></>
            )}
          </div>
          {(seasonalSpike.length !== 0 || lightningDeal.length !== 0) && (
            <>
              {allowed ? (
                <HoverBorderButton
                  disabled={!allowed || loading}
                  size="mini"
                  onClick={() => setSeasonPopup(true)}
                >
                  Edit
                </HoverBorderButton>
              ) : (
                <DarkPopup
                  trigger={
                    <div>
                      <HoverBorderButton disabled size="mini">
                        Edit
                      </HoverBorderButton>
                    </div>
                  }
                  position="top right"
                >
                  {tooltTipsText.notAllowed}
                </DarkPopup>
              )}
            </>
          )}
        </div>
      </SectionContent>
    </div>
  );
}

/*
 * A number input with its unit inside, on the right. The unit swaps for
 * `resetIcon` on hover when one is given.
 */
function SuffixedInput({
  suffix,
  disabled,
  dimmed = false,
  children,
}: {
  suffix: ReactNode;
  disabled?: boolean;
  dimmed?: boolean;
  children: ReactNode;
}) {
  return (
    <div
      className={cn(
        'sk:group/input sk:relative sk:w-[125px] sk:min-w-[125px]',
        disabled && 'sk:pointer-events-none sk:opacity-50',
        dimmed && 'sk:opacity-50',
      )}
    >
      {children}
      {suffix}
    </div>
  );
}

const INPUT_WITH_SUFFIX = 'sk:pr-14!';

function Suffix({ className, ...props }: ComponentPropsWithoutRef<'span'>) {
  return (
    <span
      className={cn(
        'sk:pointer-events-none sk:absolute sk:top-1/2 sk:right-3 sk:flex sk:-translate-y-1/2 sk:justify-end sk:text-[11px] sk:text-fg-tertiary',
        className,
      )}
      {...props}
    />
  );
}

export function CalculationAccordion({
  primaryWarehouse,
  itemUPC,
  refreshTimeline,
  national,
  shipping_buffer,
  currentBuffer,
  readOnly,
  moq,
  itemId,
  defaultValues,
  setDefaultValues,
  setLoading,
  loading,
  isDefault,
}) {
  const [focused, setFocused] = useState({});
  const [formValue, setFormValue] = useState({
    moq: 0,
    lead_time: 0,
    transit_time: 0,
    minimum_desired_stock: 0,
  });

  const toggleFocus = (key, value) => {
    setFocused(focused => ({
      ...focused,
      [key]: value,
    }));
  };

  useEffect(() => {
    const defaultValue = {
      transit_time:
        (national
          ? shipping_buffer?.national
          : shipping_buffer?.international) || '',
      moq: moq || 0,
      minimum_desired_stock: currentBuffer.minimum_desired_stock || 0,
      lead_time: currentBuffer?.lead_time || 0,
    };

    setFormValue(defaultValue);
    setDefaultValues(defaultValue);
  }, [shipping_buffer, national, currentBuffer]);

  const numbersAreEqual = (num1, num2) =>
    parseFloat(num1 || 0) === parseFloat(num2 || 0);

  const transitTimeChanged = useMemo(
    () =>
      !numbersAreEqual(
        formValue.transit_time,
        defaultValues.transit_time || currentBuffer.transit_time,
      ),
    [formValue, defaultValues],
  );
  const moqChanged = useMemo(
    () => !numbersAreEqual(formValue.moq, defaultValues.moq),
    [formValue, defaultValues],
  );
  const minDesiredStockChanged = useMemo(
    () =>
      !numbersAreEqual(
        formValue.minimum_desired_stock,
        defaultValues.minimum_desired_stock,
      ),
    [formValue, defaultValues],
  );
  const leadTimeChanged = useMemo(
    () => !numbersAreEqual(formValue.lead_time, defaultValues.lead_time),
    [formValue, defaultValues],
  );

  const changed = useMemo(
    () =>
      transitTimeChanged ||
      moqChanged ||
      minDesiredStockChanged ||
      leadTimeChanged,
    [transitTimeChanged, moqChanged, minDesiredStockChanged, leadTimeChanged],
  );

  const updateVariables = async () => {
    try {
      setLoading(true);
      const payload = {
        shipping_buffer: {
          // @ts-expect-error - TS2345: Argument of type 'number' is not assignable to parameter of type 'string'.
          national: national ? parseFloat(formValue?.transit_time || 0) : 7,
          international: national
            ? 30
            : // @ts-expect-error - TS2345: Argument of type 'number' is not assignable to parameter of type 'string'.
              parseFloat(formValue?.transit_time || 0),
        },
        upc: itemUPC,
        provider_id: primaryWarehouse,
        lead_time: formValue.lead_time,
        MOQ: formValue.moq,
        item_id: itemId,
      };
      if (minDesiredStockChanged) {
        await updateMinimumDesiredStock();
      }
      if (transitTimeChanged || moqChanged || leadTimeChanged) {
        await PredictionService.updateItemVariables(payload);
      }
      await refreshTimeline();

      ToastMessage.success('Provided details updated successfully.');
    } catch (error) {
      ToastMessage.error(error.error || ErrorMessageConstant.defaultAPIError);
      setLoading(false);
    }
  };

  const updateMinimumDesiredStock = async () => {
    try {
      const reqBody = {};
      // @ts-expect-error - TS2339: Property 'minimum_desired_stock' does not exist on type '{}'.
      reqBody.minimum_desired_stock = [
        {
          provider_id: primaryWarehouse,
          stock: formValue?.minimum_desired_stock,
        },
      ];
      await ItemService.updateMinimumStock(itemId, reqBody);
    } catch (error) {
      ToastMessage.apiError(error);
    }
  };

  const resetMinDesiredStock = async () => {
    setLoading(true);
    const reqBody = {};
    // @ts-expect-error - TS2339: Property 'minimum_desired_stock' does not exist on type '{}'.
    reqBody.minimum_desired_stock = [
      {
        provider_id: primaryWarehouse,
        stock: 0,
      },
    ];
    try {
      await ItemService.updateMinimumStock(itemId, reqBody);
      await refreshTimeline();

      ToastMessage.success(
        'Minimum desired stock has been successfully reset.',
      );
    } catch (error) {
      ToastMessage.apiError(error);
      setLoading(false);
    }
  };

  const updateField = (key, value) => {
    setFormValue({
      ...formValue,
      [key]: value === '' ? value : +value,
    });
  };

  return (
    <div className="sk:flex! sk:flex-col!">
      <h4 className="sk:flex sk:items-center">
        Calculation Variables
        <DarkPopup
          trigger={<Info className="sk:ml-2!" />}
          hover
          position="right center"
        >
          {tooltTipsText.calcVariables}
        </DarkPopup>{' '}
      </h4>
      <div className="sk:flex sk:w-[290px] sk:min-w-[290px] sk:flex-wrap sk:gap-3">
        <div className="sk:flex! sk:flex-col!">
          <MuteText className="sk:text-xs">MOQ</MuteText>
          <SuffixedInput disabled={loading} suffix={<Suffix>Units</Suffix>}>
            <NumberMaskInput
              placeholder="MOQ"
              className={INPUT_WITH_SUFFIX}
              maskOptions={{
                allowDecimal: true,
                // @ts-expect-error - TS2353: Object literal may only specify known properties, and 'decimalLimit' does not ex...
                decimalLimit: 3,
                decimalSymbol: '.',
              }}
              disabled={readOnly}
              onChange={val => updateField('moq', val)}
              value={formValue?.moq}
            />
          </SuffixedInput>
        </div>
        <div className="sk:flex! sk:flex-col!">
          <MuteText className="sk:text-xs">Lead Time</MuteText>
          <SuffixedInput disabled={loading} suffix={<Suffix>Days</Suffix>}>
            <NumberMaskInput
              placeholder="Lead Time"
              className={INPUT_WITH_SUFFIX}
              maskOptions={{
                allowDecimal: true,
                // @ts-expect-error - TS2353: Object literal may only specify known properties, and 'decimalLimit' does not ex...
                decimalLimit: 3,
                decimalSymbol: '.',
              }}
              disabled={readOnly}
              onChange={val => updateField('lead_time', val)}
              value={formValue?.lead_time}
            />
          </SuffixedInput>
        </div>
        <div className="sk:flex! sk:flex-col!">
          <MuteText className="sk:flex sk:items-center sk:text-xs">
            Min. Desired Stock
            <DarkPopup
              trigger={<Info className="sk:ml-1!" />}
              hover
              position="right center"
            >
              {tooltTipsText.minDesiredStock}
            </DarkPopup>
          </MuteText>
          <SuffixedInput
            disabled={loading}
            dimmed={
              !(
                minDesiredStockChanged ||
                !isDefault.source_minimum_buffer ||
                // @ts-expect-error - TS2339: Property 'minimumStock' does not exist on type '{}'.
                focused.minimumStock
              )
            }
            suffix={
              <Suffix
                className={cn(
                  'sk:min-w-11',
                  !isDefault.source_minimum_buffer && 'sk:pointer-events-auto',
                )}
              >
                <span
                  className={cn(
                    !isDefault.source_minimum_buffer &&
                      'sk:group-hover/input:hidden',
                  )}
                >
                  {isDefault.source_minimum_buffer &&
                  // @ts-expect-error - TS2339: Property 'minimumStock' does not exist on type '{}'.
                  !focused.minimumStock &&
                  !minDesiredStockChanged
                    ? '(Default) '
                    : ''}
                  Days
                </span>
                {!isDefault.source_minimum_buffer && (
                  <ArrowCounterClockwise
                    onClick={resetMinDesiredStock}
                    size={13}
                    aria-label="Reset to default"
                    className="sk:hidden sk:cursor-pointer sk:group-hover/input:block"
                  />
                )}
              </Suffix>
            }
          >
            <NumberMaskInput
              placeholder="Minimum Desired Stock"
              maskOptions={{
                allowDecimal: true,
                // @ts-expect-error - TS2353: Object literal may only specify known properties, and 'decimalLimit' does not ex...
                decimalLimit: 3,
                decimalSymbol: '.',
              }}
              className="sk:pr-[4.5rem]!"
              disabled={readOnly}
              onChange={val => updateField('minimum_desired_stock', val)}
              value={formValue?.minimum_desired_stock}
              onFocus={() => toggleFocus('minimumStock', true)}
              onBlur={() => toggleFocus('minimumStock', false)}
            />
          </SuffixedInput>
        </div>
        <div className="sk:flex! sk:flex-col!">
          <MuteText className="sk:text-xs">Transit Time</MuteText>
          <SuffixedInput disabled={loading} suffix={<Suffix>Days</Suffix>}>
            <NumberMaskInput
              maskOptions={{
                allowDecimal: true,
                // @ts-expect-error - TS2353: Object literal may only specify known properties, and 'decimalLimit' does not ex...
                decimalLimit: 3,
                decimalSymbol: '.',
              }}
              disabled={readOnly}
              onChange={val => updateField('transit_time', val)}
              value={formValue?.transit_time}
              placeholder={`${currentBuffer.transit_time || 0} (Default)`}
              className={INPUT_WITH_SUFFIX}
              maxValue={1000}
            />
          </SuffixedInput>
        </div>
        {changed && !loading && (
          <StyledButton onClick={() => updateVariables()} size="tiny">
            Update
          </StyledButton>
        )}
      </div>
    </div>
  );
}

export function RegionalAccordion({
  tabs,
  toggleTab,
  timeline,
  regionalFormattedData,
  regions,
}) {
  const regionList = useMemo(() => {
    const list = {};

    if (CommonUtility.isValidArray(regions)) {
      regions.forEach(
        ({
          marketplace_region_name: name = '',
          marketplace_region_id: id = '',
        }) => {
          if (name && id) {
            list[name.toUpperCase()] = id;
          }
        },
      );
    }

    return list;
  }, [regions.length]);

  const CaretIcon = tabs.regionalBreakdown ? CaretDown : CaretRight;

  if (!CommonUtility.isValidArray(timeline)) {
    return null;
  }

  return (
    <div>
      <SectionTitle className="sk:mb-2">
        <h4>Regional Breakdown</h4>
        <CaretIcon
          onClick={() =>
            toggleTab('regionalBreakdown', !tabs.regionalBreakdown)
          }
        />
      </SectionTitle>
      <SectionContent active={tabs.regionalBreakdown}>
        <div className="sk:mb-3 sk:flex sk:flex-wrap sk:gap-[2.25em] sk:pb-3.5 sk:opacity-60">
          <div className="sk:flex sk:items-center">
            <ManualStockIcon />
            &nbsp;Current Stock
            <DarkPopup trigger={<Info className="sk:ml-2!" />} hover>
              {tooltTipsText.currentStock} in a region.
            </DarkPopup>
          </div>
          <div className="sk:items-center! sk:flex!">
            <ShoppingCart size={20} />
            &nbsp;Suggested Order
            <DarkPopup
              trigger={<Info className="sk:ml-2!" />}
              position="right center"
              hoverable
            >
              {tooltTipsText.genericSuggestedOrder}
            </DarkPopup>
          </div>
          <div className="sk:flex! sk:items-center!">
            <Truck size={20} />
            &nbsp; Expected Inventory
            <DarkPopup
              trigger={<Info className="sk:ml-2!" />}
              position="right center"
              hoverable
            >
              {tooltTipsText.expected_inventory} specific region.
            </DarkPopup>
          </div>
          <div className="sk:items-center! sk:flex!">
            <div className="sk:size-[18px] sk:rounded-[3px] sk:border sk:border-primary">
              <Check size={16} />
            </div>
            &nbsp; Enable/Disable Predictions for Region
          </div>
        </div>
        <div className="sk:mt-[1em] sk:flex sk:flex-wrap sk:justify-start sk:gap-x-[3em] sk:gap-y-[1.5em]">
          {timeline?.map(region => (
            <Region
              // @ts-expect-error - TS18046: 'region' is of type 'unknown'.
              regionalFormattedData={regionalFormattedData[region.region_name]}
              data={region}
              regionList={regionList}
            />
          ))}
        </div>
      </SectionContent>
    </div>
  );
}

function StorageProviderDetails({ className, ...props }: DivProps) {
  return (
    <div
      className={cn('sk:[&_span]:whitespace-nowrap', className)}
      {...props}
    />
  );
}

function Region({ data, regionalFormattedData, regionList = {} }) {
  const providers = useMemo(
    () =>
      data.fulfillment_providers?.filter(d => {
        if (data.region_name) {
          const regionId = regionList?.[data?.region_name?.toUpperCase()];
          if (regionId && d.marketplace_region_id)
            return regionId === d.marketplace_region_id;
        }
        return true;
      }),
    [data?.fulfillment_providers],
  );

  const sortedProviders = useMemo(
    () => providers?.sort((fp1, fp2) => fp2.inventory - fp1.inventory),
    [providers],
  );

  return (
    <div className={cn(regionalFormattedData.maxQty === 0 && 'sk:opacity-60')}>
      <div className="sk:flex! sk:flex-col!">
        <div className="sk:flex! sk:items-center! sk:justify-between!">
          {data.region_name && (
            <div className="sk:flex! sk:items-center! sk:mr-3.5!">
              <Flag name={data.region_name} />
              <b>{data.region_name}</b>
            </div>
          )}
          <SC
            className="sk:pointer-events-none"
            checked={!!regionalFormattedData.maxQty}
          />
        </div>
        <VelocityDetails className="sk:flex sk:items-center">
          {CommonUtility.roundNumber(regionalFormattedData.maxVelocity, 2)}{' '}
          <MuteText>&nbsp;Units&nbsp;/&nbsp;Day</MuteText>
          <DarkPopup
            trigger={<Info className="sk:ml-2!" />}
            position="right center"
            hoverable
          >
            <div className="sk:flex! sk:justify-between! sk:w-full sk:p-2! sk:mb-2!">
              <MuteText className="sk:mr-5!">Storage Provider</MuteText>
              <MuteText className="sk:ml-5!">Unit/Day</MuteText>
            </div>
            {sortedProviders?.map(provider => (
              <StorageProviderDetails
                key={provider.fulfillment_provider_id}
                className="sk:px-2! sk:mb-2! sk:flex! sk:justify-between! sk:w-full"
              >
                <span className="sk:mr-5!">
                  {provider.fulfillment_provider_name}
                </span>
                <span className="sk:ml-5!">
                  {provider.adjusted_velocity || provider.sales_velocity}
                </span>
              </StorageProviderDetails>
            ))}
          </DarkPopup>
        </VelocityDetails>
      </div>
      <div className="sk:flex! sk:my-1! sk:items-center!">
        <ManualStockIcon className="sk:mr-2" />
        {CommonUtility.numberWithCommas(regionalFormattedData.totalInventory)}{' '}
        Units{' '}
        <MuteText>
          &nbsp;(
          {CommonUtility.numberWithCommas(
            regionalFormattedData.providerDaysLeft,
          )}{' '}
          Days)
        </MuteText>
        <DarkPopup
          trigger={<Info className="sk:ml-2!" />}
          position="right center"
          hoverable
        >
          <div className="sk:flex! sk:justify-between! sk:w-full sk:p-2! sk:mb-2!">
            <MuteText className="sk:mr-5! sk:whitespace-nowrap">
              Storage Provider
            </MuteText>
            <MuteText className="sk:ml-5! sk:text-right sk:whitespace-nowrap">
              Days (Units / Velocity)
            </MuteText>
          </div>
          {providers.map(provider => {
            const {
              adjusted_velocity,
              fulfillment_provider_id,
              fulfillment_provider_name,
              inventory,
              sales_velocity,
              reserved_inventory,
            } = provider;

            const velocity = adjusted_velocity || sales_velocity;
            const units = CommonUtility.numberWithCommas(
              inventory + (reserved_inventory || 0),
            );
            const days = velocity
              ? CommonUtility.numberWithCommas(
                  +parseFloat(
                    // @ts-expect-error - TS2345: Argument of type 'number' is not assignable to parameter of type 'string'.
                    (inventory + (reserved_inventory || 0)) / velocity,
                  ).toFixed(0),
                )
              : 0;
            // @ts-expect-error - TS18048: 'days' is possibly 'undefined'.
            const daysLabel = days > 1 ? 'Days' : 'Day';

            return (
              <StorageProviderDetails
                key={fulfillment_provider_id}
                className="sk:px-2! sk:mb-2! sk:flex! sk:justify-between! sk:w-full"
              >
                <span className="sk:mr-5!">{fulfillment_provider_name}</span>
                <span className="sk:ml-5!">
                  {days} <MuteText>{daysLabel}</MuteText> ({units}{' '}
                  <MuteText>Units</MuteText> / {velocity})
                </span>
              </StorageProviderDetails>
            );
          })}
          <div className="sk:p-2!">
            <MuteText>
              Includes all the stock across storage providers and fulfillment
              providers in {data.region_name}.
            </MuteText>
          </div>
        </DarkPopup>
      </div>
      <div className="sk:flex! sk:items-center!">
        <ShoppingCart className="sk:mr-2!" size={20} />
        {CommonUtility.numberWithCommas(regionalFormattedData.maxQty)} Units{' '}
        <MuteText>
          &nbsp;(
          {CommonUtility.numberWithCommas(
            regionalFormattedData.predictedDaysLeft,
          )}{' '}
          Days)
        </MuteText>
        <DarkPopup
          trigger={<Info className="sk:ml-2!" />}
          position="right center"
          hoverable
        >
          This is the predicted purchase order quantity for {data.region_name}.
          Please view equation below for the detailed breakdown of parameters
          used for the calculation.
        </DarkPopup>
      </div>
      <div className="sk:flex! sk:items-center!">
        <Truck className="sk:mr-2!" size={20} />
        {CommonUtility.numberWithCommas(data.expected_inventory)} Units
        <DarkPopup
          trigger={<Info className="sk:ml-2!" />}
          position="right center"
          hoverable
        >
          {tooltTipsText.expected_inventory} {data.region_name}.
        </DarkPopup>
      </div>
    </div>
  );
}

export function VelocityAccordion({
  primaryVelocities,
  itemId,
  refresh,
  title = 'Global Velocity Calculation',
  readOnly,
  setLoading,
  loading,
  data,
}) {
  return (
    <div className="sk:w-full">
      <div>
        {Object.keys(primaryVelocities).length > 0 ? (
          <POWindowVelocityRenderer
            data={data}
            hardRefreshLoading={readOnly || loading}
            context="PO"
            loading={loading}
            setLoading={setLoading}
            refreshTimeline={refresh}
            itemId={itemId}
            title={title}
          />
        ) : (
          <div>
            <MuteText>No Data</MuteText>
          </div>
        )}
      </div>
    </div>
  );
}

function VelocityDetails({ className, ...props }: DivProps) {
  return <div className={cn('sk:text-[11px]', className)} {...props} />;
}

function ContainerFormula({ className, ...props }: DivProps) {
  return (
    <div
      className={cn(
        'sk:flex sk:items-center sk:text-[11px] sk:[&_span]:whitespace-nowrap',
        className,
      )}
      {...props}
    />
  );
}

function PrimaryWarehouseExistingPOs({
  predictedOrderBuffer,
  inventory_credits,
  velocityProvider,
}) {
  const inventoryCreditDetails =
    predictedOrderBuffer?.inventoryCreditDetails?.[0] || {};

  const hasPOs = CommonUtility.isValidArray(inventoryCreditDetails?.PODetails);
  const hasShipments = CommonUtility.isValidArray(
    inventoryCreditDetails?.POShipmentDetails,
  );

  const shipmentTotal = useMemo(
    () =>
      inventoryCreditDetails?.POShipmentDetails?.reduce(
        (total, shipment) => total + (shipment.inventoryShipment || 0),
        0,
      ),
    [inventoryCreditDetails],
  );

  if (!predictedOrderBuffer) return 'Not Enough Data';

  return (
    <StyledPopup>
      <div>
        <span>Sales Velocity</span>
        <span>=</span>
        <span>{velocityProvider}</span>
        <MuteText>units / day</MuteText>
      </div>
      <div className="sk:mb-3.5!">
        <span>Quantity in Days of {inventoryCreditDetails?.warehouseName}</span>
        <span>=</span>
        <span>
          {inventoryCreditDetails.inventoryWarehouse}
          <MuteText>units</MuteText>/
        </span>
        <span>{velocityProvider}</span>
        <span>=</span>
        <span>
          <RenderMutedDays
            days={CommonUtility.roundNumber(
              inventoryCreditDetails.creditWarehouse,
              1,
            )}
          />
        </span>
      </div>
      <table cellSpacing="0">
        <tr>
          <td>
            <b>Primary Warehouse</b>
          </td>
          <td />
          <td />
        </tr>
        <tr>
          <td>{inventoryCreditDetails?.warehouseName}</td>
          <td>
            {inventoryCreditDetails.inventoryWarehouse}
            <MuteText>units</MuteText>
          </td>
          <td>
            (
            <RenderMutedDays
              days={CommonUtility.roundNumber(
                inventoryCreditDetails?.creditWarehouse,
                1,
              )}
            />
            )
          </td>
        </tr>

        {hasPOs && (
          <tr>
            <td className="sk:pt-3.5!">
              <b>Purchase Orders</b>
            </td>
            <td />
            <td />
          </tr>
        )}
        {inventoryCreditDetails?.PODetails.map(po => (
          <tr>
            <td>{po.poDisplayId}</td>
            <td>
              {po.inventoryPO}
              <MuteText>units</MuteText>
            </td>
            <td>
              (
              <RenderMutedDays
                days={CommonUtility.roundNumber(po.creditPO, 1)}
              />
              )
            </td>
          </tr>
        ))}
        {hasPOs && (
          <>
            <tr>
              <td className="total">Purchase Order Total</td>
              <td className="sk:mx-5! total">
                {inventoryCreditDetails?.inventoryPOTotal}
                <MuteText>units</MuteText>
              </td>
              <td className="total">
                (
                <RenderMutedDays
                  days={CommonUtility.roundNumber(
                    inventoryCreditDetails?.creditPOTotal,
                    1,
                  )}
                />
                )
              </td>
            </tr>
          </>
        )}
        {hasShipments && (
          <tr>
            <td className="sk:pt-3.5!">
              <b>Shipments</b>
            </td>
            <td />
            <td />
          </tr>
        )}
        {inventoryCreditDetails?.POShipmentDetails?.map(shipment => (
          <tr>
            <td>{shipment.shipmentDisplayId}</td>
            <td className="sk:mx-5!">
              {shipment.inventoryShipment}
              <MuteText>units</MuteText>
            </td>
            <td>
              (
              <RenderMutedDays
                days={CommonUtility.roundNumber(
                  shipment.creditInventoryShipment,
                  1,
                )}
              />
              )
            </td>
          </tr>
        ))}
        {hasShipments && (
          <tr>
            <td className="total">Shipment Total</td>
            <td className="sk:mx-5! total">
              {shipmentTotal}
              <MuteText>units</MuteText>
            </td>
            <td className="total">
              (
              <RenderMutedDays
                days={CommonUtility.roundNumber(
                  inventoryCreditDetails?.POShipmentCredit,
                  1,
                )}
              />
              )
            </td>
          </tr>
        )}
      </table>
      <div className="sk:mt-3.5! sk:flex!">
        <span>Primary Warehouse Inventory, Existing POs and Shipments</span>
        <span>=</span>
        <span>Primary Warehouse</span>
        {hasPOs && (
          <>
            <span>+</span>
            <span>Purchase Order Total</span>
          </>
        )}
        {hasShipments && (
          <>
            <span>+</span>
            <span>Shipment Total</span>
          </>
        )}
      </div>
      <div>
        <span>
          <RenderMutedDays
            days={CommonUtility.numberWithCommas(inventory_credits)}
          />
        </span>
        <span>=</span>
        <span>
          <RenderMutedDays
            days={CommonUtility.roundNumber(
              inventoryCreditDetails?.creditWarehouse,
              1,
            )}
          />
        </span>
        {hasPOs && (
          <>
            <span>+</span>
            <span>
              <RenderMutedDays
                days={CommonUtility.roundNumber(
                  inventoryCreditDetails?.creditPOTotal,
                  1,
                )}
              />
            </span>
          </>
        )}
        {hasShipments && (
          <>
            <span>+</span>
            <span>
              <RenderMutedDays
                days={CommonUtility.roundNumber(
                  inventoryCreditDetails?.POShipmentCredit,
                  1,
                )}
              />
            </span>
          </>
        )}
      </div>
    </StyledPopup>
  );
}

const displayNumber = value => CommonUtility.roundNumber(parseNumber(value), 1);

function OtherWarehouseInventory({
  predictedOrderBuffer,
  velocityProvider,
  other_warehouse_credit,
}) {
  if (!predictedOrderBuffer) return 'Not Enough Data';

  const hasPOShipmentDetails = CommonUtility.isValidArray(
    predictedOrderBuffer?.otherWarehouseCreditDetail?.[0]?.POShipmentDetails,
  );

  const totalWarehouseCredits = useMemo(() => {
    let total = 0;
    predictedOrderBuffer?.otherWarehouseCreditDetail?.forEach(warehouse => {
      total += warehouse.creditWarehouse - (warehouse.POShipmentCredit || 0);
    });
    return CommonUtility.roundNumber(total);
  }, [predictedOrderBuffer]);

  const totalShipmentCredits = useMemo(() => {
    let total = 0;
    predictedOrderBuffer?.otherWarehouseCreditDetail?.forEach(warehouse => {
      total += warehouse.POShipmentCredit || 0;
    });
    return CommonUtility.roundNumber(total);
  }, [predictedOrderBuffer]);

  return (
    <StyledPopup>
      <div className="sk:mb-2!">
        <span>Sales Velocity</span>
        <span>=</span>
        <span>{velocityProvider}</span>
        <MuteText>units / day</MuteText>
      </div>
      {predictedOrderBuffer?.otherWarehouseCreditDetail?.map(warehouse => (
        <div>
          <span>Quantity in Days of {warehouse.warehouseName}</span>
          <span>=</span>
          <span>
            {displayNumber(warehouse.inventoryWarehouse)}
            <MuteText>units</MuteText>/
          </span>
          <span>{velocityProvider}</span>
          <span>=</span>
          <span>
            <RenderMutedDays
              days={displayNumber(
                warehouse.creditWarehouse - (warehouse.POShipmentCredit || 0),
              )}
            />
          </span>
        </div>
      ))}
      <table cellSpacing="0" className="sk:mt-3.5!">
        <tr>
          <td>
            {' '}
            <b>Warehouses</b>{' '}
          </td>
          <td />
          <td />
        </tr>
        {predictedOrderBuffer?.otherWarehouseCreditDetail?.map(warehouse => (
          <tr>
            <td>{warehouse.warehouseName}</td>
            <td>
              {displayNumber(warehouse.inventoryWarehouse)}
              <MuteText>units</MuteText>
            </td>
            <td>
              (
              <RenderMutedDays
                days={displayNumber(
                  warehouse.creditWarehouse - (warehouse.POShipmentCredit || 0),
                )}
              />
              )
            </td>
          </tr>
        ))}
        <tr>
          <td className="total">
            <SuccessDarkText className="sk:m-0! sk:p-0!">
              Warehouse Total
            </SuccessDarkText>
          </td>
          <td className="sk:mx-5! total">
            {displayNumber(
              predictedOrderBuffer?.otherWarehouseCreditDetail?.[0]
                ?.inventoryTotalOtherWarehouse,
            )}
            <MuteText>units</MuteText>
          </td>
          <td className="total">
            <SuccessDarkText className="sk:m-0! sk:p-0!">
              (
              <RenderMutedDays
                renderMuted={false}
                days={displayNumber(totalWarehouseCredits)}
              />
              )
            </SuccessDarkText>
          </td>
        </tr>
      </table>
      {hasPOShipmentDetails && (
        <table cellSpacing="0" className="sk:mt-2!">
          <tr>
            <td>
              <b>Shipments</b>
            </td>
            <td />
            <td />
          </tr>
          {predictedOrderBuffer?.otherWarehouseCreditDetail?.map(warehouse => {
            if (CommonUtility.isValidArray(warehouse.POShipmentDetails)) {
              return (
                <>
                  {warehouse.POShipmentDetails.map(shipment => (
                    <tr>
                      <td>{shipment.shipmentDisplayId}</td>
                      <td>
                        {displayNumber(shipment.inventoryShipment)}
                        <MuteText>units</MuteText>
                      </td>
                      <td>
                        (
                        <RenderMutedDays
                          days={displayNumber(shipment.creditInventoryShipment)}
                        />
                        )
                      </td>
                    </tr>
                  ))}
                </>
              );
            }
            return <></>;
          })}
          <tr>
            <td className="total">
              <BlueDarkText className="sk:m-0! sk:p-0!">
                Shipment Credit
              </BlueDarkText>
            </td>
            <td className="sk:mx-5! total" />
            <td className="total">
              <BlueDarkText className="sk:m-0! sk:p-0!">
                (
                <RenderMutedDays
                  renderMuted={false}
                  days={displayNumber(totalShipmentCredits)}
                />
                )
              </BlueDarkText>
            </td>
          </tr>
        </table>
      )}
      {hasPOShipmentDetails ? (
        <div>
          <div className="sk:mt-3.5! sk:flex!">
            <span>Other Warehouse Inventory</span>
            <span>=</span>
            <SuccessDarkText className="sk:m-0! sk:p-0!">
              Warehouse Total
            </SuccessDarkText>
            <span>+</span>
            <BlueDarkText className="sk:m-0! sk:p-0!">
              Shipment Credit
            </BlueDarkText>
          </div>
          <div className="sk:flex!">
            <span>Other Warehouse Inventory</span>
            <span>=</span>
            <SuccessDarkText className="sk:m-0! sk:p-0!">
              <RenderMutedDays
                renderMuted={false}
                days={CommonUtility.numberWithCommas(totalWarehouseCredits)}
              />
            </SuccessDarkText>
            <span>+</span>
            <BlueDarkText className="sk:m-0! sk:p-0!">
              <RenderMutedDays
                renderMuted={false}
                days={CommonUtility.numberWithCommas(totalShipmentCredits)}
              />
            </BlueDarkText>
            <span>=</span>
            <PinkText>{other_warehouse_credit} Days</PinkText>
          </div>
        </div>
      ) : (
        <div className="sk:mt-3.5! sk:flex!">
          <span>Other Warehouse Inventory</span>
          <span>=</span>
          <SuccessDarkText className="sk:m-0! sk:p-0!">
            Warehouse Total
          </SuccessDarkText>
          <span>=</span>
          <PinkText className="sk:m-0! sk:p-0!">
            <RenderMutedDays
              renderMuted={false}
              days={CommonUtility.numberWithCommas(totalWarehouseCredits)}
            />
          </PinkText>
        </div>
      )}
    </StyledPopup>
  );
}

const numberAbs = value => CommonUtility.roundNumber(Math.abs(value), 1);

export function FormulaNoData() {
  return (
    <div className="sk:flex! sk:items-center! sk:justify-center! sk:pt-5!">
      <StyledFormTitle>No Formula Data</StyledFormTitle>
    </div>
  );
}

export function Formula({
  adjusted = false,
  aliasName,
  currentBuffer,
  defaultMinDesiredStock,
  defaultValues,
  destinationName,
  predictedOrder,
  suggestedQty,
  unitsPerCarton,
  velocityProvider,
  showMessage = false,
  fullOpacity = false,
  smallTooltips = false,
}) {
  const predictedOrderBuffer = predictedOrder?.buffer_data;
  const childPredictedOrders = predictedOrder?.order_prediction_child || [];
  const [isCopied, setIsCopied] = useState(false);

  const {
    other_fp_credit,
    inventory_credits,
    other_warehouse_credit,
    existing_inventory,
  } = useMemo(() => {
    const order = predictedOrderBuffer;
    const current = currentBuffer;

    const FPCreditDetails = order?.FPCreditDetails ?? current?.FPCreditDetails;
    const inventoryCredit =
      order?.inventoryCreditDetails ?? current?.inventoryCreditDetails;
    const otherWarehouse =
      order?.otherWarehouseCreditDetail ?? current?.otherWarehouseCreditDetail;

    let inventory_credits = 0;
    let other_fp_credit = 0;
    let other_warehouse_credit = 0;

    if (CommonUtility.isValidArray(FPCreditDetails)) {
      // @ts-expect-error - TS2339: Property 'creditUsedFromProvider' does not exist on type '{}'.
      FPCreditDetails.filter(d => d?.creditUsedFromProvider > 0).forEach(
        // @ts-expect-error - TS2339: Property 'creditUsedFromProvider' does not exist on type '{}'.
        d => (other_fp_credit += d?.creditUsedFromProvider),
      );
    }

    if (CommonUtility.isValidArray(inventoryCredit)) {
      inventoryCredit
        // @ts-expect-error - TS2339: Property 'creditWarehouse' does not exist on type '{}'.
        .filter(d => d?.creditWarehouse > 0 || d?.creditPOTotal > 0)
        .forEach(d => {
          // @ts-expect-error - TS2339: Property 'creditWarehouse' does not exist on type '{}'.
          if (d?.creditWarehouse > 0) inventory_credits += d?.creditWarehouse;
          // @ts-expect-error - TS2339: Property 'creditPOTotal' does not exist on type '{}'.
          if (d?.creditPOTotal > 0) inventory_credits += d?.creditPOTotal;
        });
    }

    if (CommonUtility.isValidArray(otherWarehouse)) {
      otherWarehouse
        // @ts-expect-error - TS2339: Property 'creditWarehouse' does not exist on type '{}'.
        .filter(d => d?.creditWarehouse > 0)
        // @ts-expect-error - TS2339: Property 'creditWarehouse' does not exist on type '{}'.
        .forEach(d => (other_warehouse_credit += d?.creditWarehouse));
    }

    return {
      other_fp_credit: numberAbs(other_fp_credit),
      inventory_credits: numberAbs(inventory_credits),
      other_warehouse_credit: numberAbs(other_warehouse_credit),
      existing_inventory: -numberAbs(
        other_fp_credit + inventory_credits + other_warehouse_credit,
      ),
    };
  }, [predictedOrderBuffer]);

  const lead_time = defaultValues?.lead_time || 0;
  const transit_time =
    (currentBuffer?.inbound_buffer || 0) + (defaultValues?.transit_time || 0);
  const minDesiredStock =
    currentBuffer?.minimum_desired_stock || defaultMinDesiredStock || 0;

  const calcBeforeRounding = useMemo(() => {
    let temp =
      (minDesiredStock + lead_time + transit_time + existing_inventory) *
      velocityProvider;
    childPredictedOrders?.forEach(
      childPredictedOrder => (temp += childPredictedOrder?.qty),
    );
    return CommonUtility.roundNumber(temp, 1);
  }, [
    minDesiredStock,
    lead_time,
    transit_time,
    existing_inventory,
    velocityProvider,
    childPredictedOrders,
  ]);

  const beforeRounding = calcBeforeRounding > 0 ? calcBeforeRounding : 0;

  const onCopyClicked = () => {
    setIsCopied(true);
    let toSave = `${beforeRounding} Units = (( ${lead_time} Days + ${transit_time} Days`;
    if (minDesiredStock) {
      toSave += ` + ${minDesiredStock} Days `;
    }
    if (existing_inventory) {
      toSave += ` ${existing_inventory < 0 ? '-' : '+'} `;
      toSave += ` ${Math.abs(existing_inventory)} Days`;
    }
    toSave += ` ) x ${velocityProvider} Units/Day )`;
    childPredictedOrders?.forEach(childPredicted => {
      toSave += ` + ${Math.round(childPredicted.qty)} Units`;
    });
    CommonUtility.copyToClipboard(toSave);
    setTimeout(() => {
      setIsCopied(false);
    }, 1500);
  };

  const flags = {
    otherFpCredit: other_fp_credit !== undefined,
    inventoryCredits: inventory_credits !== undefined,
    otherWarehouseCredit: other_warehouse_credit !== undefined,
  };

  const oneVariablePresent =
    flags.otherFpCredit || flags.inventoryCredits || flags.otherWarehouseCredit;

  if (suggestedQty === undefined) {
    if (showMessage) {
      return <span>No Data</span>;
    }
    return null;
  }

  return (
    <FlexColumn
      className={cn(
        'sk:items-start sk:hover:opacity-100',
        !((suggestedQty || fullOpacity) > 0) && 'sk:opacity-60',
      )}
    >
      {oneVariablePresent && (
        <FCInventorySummary
          other_fp_credit={other_fp_credit}
          velocityProvider={velocityProvider}
          predictedOrderBuffer={predictedOrderBuffer}
        />
      )}
      {oneVariablePresent && (
        <ContainerFormula className="sk:mb-1">
          <MuteText>Existing Inventory POs and Shipments</MuteText>
          <span className="sk:mx-2!">=</span>
          {flags.otherFpCredit && (
            <>
              <SimpleDarkToolTip
                trigger={
                  <BlueText className="sk:px-1! sk:flex! sk:items-center!">
                    FC Inventory and Shipments{' '}
                    <Info size={11} className="sk:ml-1!" />
                  </BlueText>
                }
                helpText={tooltTipsText.other_fp_credit}
                helpPosition="top left"
              />
              {(flags.inventoryCredits || flags.otherWarehouseCredit) && <>+</>}
            </>
          )}
          {flags.inventoryCredits && (
            <>
              <SimpleDarkToolTip
                trigger={
                  <AccentText className="sk:px-1! sk:flex! sk:items-center!">
                    Primary Warehouse Inventory, Existing POs and Shipments{' '}
                    <Info size={11} className="sk:ml-1!" />
                  </AccentText>
                }
                helpText={tooltTipsText.inventory_credits}
                helpPosition="top center"
              />
              {flags.otherWarehouseCredit && <>+</>}
            </>
          )}
          {flags.otherWarehouseCredit && (
            <SimpleDarkToolTip
              trigger={
                <PurpleText className="sk:px-1! sk:flex! sk:items-center!">
                  Other Warehouse Inventory{' '}
                  <Info size={11} className="sk:ml-1!" />
                </PurpleText>
              }
              helpText={tooltTipsText.other_warehouse_credit}
              helpPosition="top center"
            />
          )}
        </ContainerFormula>
      )}
      {oneVariablePresent && (
        <FormulaContainer className="sk:mb-2!">
          <span>
            {CommonUtility.numberWithCommas(Math.abs(existing_inventory))} Days
          </span>
          <span>=</span>
          {flags.otherFpCredit && (
            <>
              <SimpleDarkToolTip
                trigger={
                  <BlueText>
                    {CommonUtility.numberWithCommas(other_fp_credit)} Days
                    <Info size={11} className="sk:ml-1!" />
                  </BlueText>
                }
                hoverable
                contentMaxHight={500}
                helpText={
                  <FCInventory
                    velocityProvider={velocityProvider}
                    other_fp_credit={other_fp_credit}
                    predictedOrderBuffer={predictedOrderBuffer}
                    smallTooltips={smallTooltips}
                  />
                }
                helpPosition="right center"
              />
              {(flags.inventoryCredits || flags.otherWarehouseCredit) && (
                <span>+</span>
              )}
            </>
          )}
          {flags.inventoryCredits && (
            <>
              <SimpleDarkToolTip
                trigger={
                  <AccentText>
                    {CommonUtility.numberWithCommas(inventory_credits)} Days
                    <Info size={11} className="sk:ml-1!" />
                  </AccentText>
                }
                hoverable
                helpText={
                  <PrimaryWarehouseExistingPOs
                    velocityProvider={velocityProvider}
                    inventory_credits={inventory_credits}
                    predictedOrderBuffer={predictedOrderBuffer}
                  />
                }
                helpPosition="top left"
              />
              {flags.otherWarehouseCredit && <span>+</span>}
            </>
          )}
          {flags.otherWarehouseCredit && (
            <SimpleDarkToolTip
              trigger={
                <PurpleText>
                  {CommonUtility.numberWithCommas(other_warehouse_credit)} Days
                  <Info size={11} className="sk:ml-1!" />
                </PurpleText>
              }
              hoverable
              helpText={
                <OtherWarehouseInventory
                  velocityProvider={velocityProvider}
                  other_warehouse_credit={other_warehouse_credit}
                  predictedOrderBuffer={predictedOrderBuffer}
                />
              }
              helpPosition="top left"
            />
          )}
        </FormulaContainer>
      )}
      <ContainerFormula className="sk:mb-1">
        {beforeRounding < 0 ? (
          <SimpleDarkToolTip
            helpText={tooltTipsText.negativeRawUnits}
            trigger={
              <MuteText className="sk:px-1! sk:flex! sk:items-center!">
                Raw Suggested Value <Info size={11} className="sk:ml-1!" />
              </MuteText>
            }
            helpPosition="top center"
          />
        ) : (
          <MuteText>Raw Suggested Value</MuteText>
        )}
        <span className="sk:mx-2!">=</span>
        ((
        <SimpleDarkToolTip
          trigger={
            <PinkText className="sk:px-1! sk:flex! sk:items-center!">
              Lead Time <Info size={11} className="sk:ml-1!" />
            </PinkText>
          }
          helpText={tooltTipsText.leadTime}
          helpPosition="top center"
        />
        +
        <SimpleDarkToolTip
          trigger={
            <AccentText className="sk:px-1! sk:flex! sk:items-center!">
              Transit and Inbound <Info size={11} className="sk:ml-1!" />
            </AccentText>
          }
          helpText={`${tooltTipsText.transitTime} added with the warehouse inbound buffer of ${destinationName}`}
          helpPosition="top center"
        />
        {!!minDesiredStock && (
          <>
            +
            <SimpleDarkToolTip
              trigger={
                <PurpleText className="sk:px-1! sk:flex! sk:items-center!">
                  Minimum Desired Stock <Info size={11} className="sk:ml-1!" />
                </PurpleText>
              }
              helpText={tooltTipsText.warehouse_buffers}
              helpPosition="top center"
            />
          </>
        )}
        {existing_inventory > 0 ? '+' : '-'}
        <SimpleDarkToolTip
          trigger={
            <span className="sk:flex sk:items-center sk:px-1 sk:text-caution">
              Existing Inventory POs and Shipments{' '}
              <Info size={11} className="sk:ml-1!" />
            </span>
          }
          helpText={tooltTipsText.existingInventory}
          helpPosition="top center"
        />
        <span>) x</span>
        <SimpleDarkToolTip
          helpText={tooltTipsText.salesVelocity}
          trigger={
            <YellowText className="sk:px-1! sk:flex! sk:items-center!">
              Sales Velocity{' '}
              {!!adjusted && (
                <MuteText className="sk:pl-2!">(Adjusted)</MuteText>
              )}
              <Info size={11} className="sk:ml-1!" />
            </YellowText>
          }
          helpPosition="top center"
        />
        )
        {childPredictedOrders?.map(childPredictedOrder => (
          <SimpleDarkToolTip
            helpText={`${tooltTipsText.childPredicted} ${childPredictedOrder.sku}`}
            trigger={
              <span className="sk:px-1! sk:flex! sk:items-center!">
                + Predicted Units from sku {childPredictedOrder.sku}
                <Info size={11} className="sk:ml-1!" />
              </span>
            }
            helpPosition="top center"
          />
        ))}
      </ContainerFormula>
      <FormulaContainer
        className={existing_inventory && oneVariablePresent ? 'sk:mb-2!' : ''}
      >
        <span>{CommonUtility.numberWithCommas(beforeRounding)} Units</span>
        <span>=</span>
        <span>((</span>
        <PinkText>{CommonUtility.numberWithCommas(lead_time)} Days</PinkText>
        <span>+</span>
        <AccentText>
          {CommonUtility.numberWithCommas(transit_time)} Days
        </AccentText>
        {!!minDesiredStock && (
          <>
            <span>+</span>
            <PurpleText>
              {CommonUtility.numberWithCommas(minDesiredStock)} Days
            </PurpleText>
          </>
        )}
        {existing_inventory > 0 ? '+' : '-'}
        {oneVariablePresent ? (
          <span className="sk:text-caution">
            {CommonUtility.numberWithCommas(Math.abs(existing_inventory))} Days
          </span>
        ) : (
          <SimpleDarkToolTip
            helpText={tooltTipsText.notEnoughDataForExisting}
            trigger={
              <span className="sk:flex sk:items-center sk:px-1 sk:text-caution">
                {CommonUtility.numberWithCommas(Math.abs(existing_inventory))}{' '}
                Days
                <Info size={11} className="sk:ml-1!" />
              </span>
            }
            helpPosition="top center"
          />
        )}
        )<span>x</span>
        <YellowText>
          {CommonUtility.numberWithCommas(velocityProvider || '0')}{' '}
          {!!adjusted && <MuteText className="sk:pl-1!">(Adjusted)</MuteText>}{' '}
          Units / Day
        </YellowText>{' '}
        )
        {childPredictedOrders?.map(childPredictedOrder => (
          <span>+ {Math.round(childPredictedOrder.qty)} Units</span>
        ))}
        {isCopied ? (
          <img
            src="/images/icon-copy-success.svg"
            height={16}
            width={16}
            className="pointer"
          />
        ) : (
          <img
            onClick={onCopyClicked}
            className="pointer"
            src="/images/icon-copy.svg"
            height={16}
            width={16}
          />
        )}
      </FormulaContainer>
      <div>
        <MuteText>Suggested Value:</MuteText>
        <b>
          &nbsp;{CommonUtility.numberWithCommas(suggestedQty)} Units ={' '}
          <SimpleDarkToolTip
            trigger={
              <span>
                Round Off ({CommonUtility.numberWithCommas(beforeRounding)}{' '}
                Units)
                <Info className="sk:pt-1!" size={15} />
              </span>
            }
            position="top center"
            helpText={
              <div>
                {tooltTipsText.cartonRounding}
                <br />
                <br />
                <span>
                  Units Per Carton of <strong>{aliasName}</strong> ={' '}
                  {unitsPerCarton}
                </span>
                {CommonUtility.isValidArray(childPredictedOrders) && (
                  <div className="sk:mt-2!">
                    <b>Prediction Breakdown</b>
                    <div className="sk:flex!">
                      <span>{aliasName}:</span>
                      <span className="sk:ml-2!">
                        {CommonUtility.numberWithCommas(
                          predictedOrder?.originQty,
                        )}
                      </span>
                      <MuteText className="sk:ml-1!">(Units)</MuteText>
                    </div>
                    {childPredictedOrders?.map(childItem => (
                      <div className="sk:flex!">
                        {/* @ts-expect-error - ts-migration */}
                        <span>{childItem.sku}:</span>
                        <span className="sk:ml-2!">
                          {CommonUtility.numberWithCommas(
                            // @ts-expect-error - ts-migration
                            childItem?.qty,
                          )}
                        </span>
                        <MuteText className="sk:ml-1!">(Units)</MuteText>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            }
          />{' '}
        </b>
      </div>
    </FlexColumn>
  );
}
